import type { Translation } from '@vlm/i18n';
import type { PlatformMessages } from './en';

/**
 * VLM Platform — العربية. Written, not translated (brand/BRAND.md §5): the glossary's words —
 * شركة، باقة، اشتراك، فاتورة، تحويل بنكي، مركبة، جهاز تتبع — and Western digits. To be read once
 * by a native-speaking reviewer before launch.
 */
export const ar: Translation<PlatformMessages> = {
  app: {
    name: 'منصّة VLM',
    skip: 'تخطَّ إلى المحتوى',
    menu: 'القائمة',
    navigation: 'التنقل الرئيسي',
  },

  common: {
    save: 'حفظ',
    saveChanges: 'حفظ التغييرات',
    cancel: 'إلغاء',
    close: 'إغلاق',
    confirm: 'تأكيد',
    edit: 'تعديل',
    delete: 'حذف',
    back: 'رجوع',
    add: 'إضافة',
    search: 'بحث',
    clearSearch: 'مسح البحث',
    retry: 'حاول مجدداً',
    loading: 'جارٍ التحميل',
    dismiss: 'إخفاء',
    actions: 'الإجراءات',
    status: 'الحالة',
    all: 'الكل',
    notes: 'ملاحظات',
    notesPlaceholder: 'أي شيء يستحق المعرفة',
    optional: 'اختياري',
    required: 'مطلوب',
    tooLong: 'بحد أقصى {max} حرفاً',
    tooShort: 'على الأقل {min} أحرف',
    invalidEmail: 'أدخل بريداً إلكترونياً مثل name@company.com',
    invalidNumber: 'أدخل رقماً',
    invalidDate: 'أدخل تاريخاً',
    notNegative: 'يجب أن يكون 0 أو أكثر',
    range: '{from}–{to} من {total}',
    previousPage: 'الصفحة السابقة',
    nextPage: 'الصفحة التالية',
    rowsPerPage: 'صفوف في الصفحة',
    noMatchTitle: 'لا توجد نتائج مطابقة',
    noMatchBody: 'جرّب بحثاً آخر، أو امسح عوامل التصفية.',
    clearFilters: 'مسح عوامل التصفية',
    loadFailedTitle: 'تعذّر التحميل',
    loadFailedBody: 'تحقق من اتصالك وحاول مجدداً.',
    activate: 'تفعيل',
    deactivate: 'إيقاف',
    active: 'فعّال',
    inactive: 'موقوف',
    never: 'أبداً',
    none: 'لا يوجد',
    view: 'عرض',
    download: 'تنزيل',
    english: 'English',
    arabic: 'العربية',
    inEnglish: 'الإنجليزية',
    inArabic: 'العربية',
    yes: 'نعم',
    no: 'لا',
    unlimited: 'بلا حدود',
    saved: 'تم الحفظ',
    language: 'اللغة',
    open: 'فتح',
  },

  nav: {
    dashboard: 'نظرة عامة',
    companies: 'الشركات',
    invoices: 'الفواتير',
    plans: 'الباقات',
    billing: 'إعدادات الفوترة',
    email: 'البريد الإلكتروني',
    users: 'مستخدمو المنصّة',
    profile: 'ملفك الشخصي',
  },

  shell: {
    theme: 'المظهر',
    themeDay: 'نهاري',
    themeNight: 'ليلي',
    themeAuto: 'حسب النظام',
    switchLanguage: 'English',
    switchLanguageLabel: 'التبديل إلى الإنجليزية',
    signOut: 'تسجيل الخروج',
    account: 'الحساب',
    openAdmin: 'فتح لوحة الإدارة',
    openWebsite: 'فتح الموقع',
    expandMenu: 'توسيع القائمة',
    collapseMenu: 'طيّ القائمة',
  },

  auth: {
    title: 'تسجيل الدخول',
    subtitle: 'منصّة VLM • لموظفي فيجانيوم فقط',
    username: 'اسم المستخدم',
    password: 'كلمة المرور',
    showPassword: 'إظهار كلمة المرور',
    hidePassword: 'إخفاء كلمة المرور',
    submit: 'تسجيل الدخول',
    usernameRequired: 'أدخل اسم المستخدم',
    passwordRequired: 'أدخل كلمة المرور',
    expired: 'انتهت جلستك. سجّل الدخول مجدداً.',
    checking: 'جارٍ تسجيل دخولك',
    unreachableTitle: 'تعذّر الوصول إلى VLM',
    unreachableBody: 'الخادم لا يستجيب. تحقق من اتصالك؛ تسجيل دخولك محفوظ.',
    scene: {
      lat: '21°25′ شمالاً',
      lng: '39°49′ شرقاً',
      scale: 'تكبير 14 • 1:34,000',
      from: 'من المستودع 3 • 08:02',
      eta: 'الوصول 10:15',
    },
  },

  roles: {
    ADMIN: 'مدير',
    STAFF: 'موظف',
  },

  roleHints: {
    ADMIN: 'كل شيء، بما في ذلك مستخدمو المنصّة وإعدادات الفوترة.',
    STAFF: 'الشركات ومستخدموها والباقات والفواتير والموقع.',
  },

  companyRoles: {
    ADMIN: 'مدير',
    OPERATOR: 'مشغّل',
    VIEWER: 'مشاهد',
  },

  companyRoleHints: {
    ADMIN: 'كل شيء في الشركة، بما في ذلك مستخدموها وإعدادات التنبيهات والفوترة.',
    OPERATOR: 'المركبات وأجهزة التتبع والربط والأسيجة الجغرافية والتنبيهات.',
    VIEWER: 'يرى الخريطة المباشرة والرحلات والتنبيهات والتقارير.',
  },

  companyStatus: {
    PENDING: 'بانتظار الموافقة',
    ACTIVE: 'فعّالة',
    SUSPENDED: 'معلّقة',
    REJECTED: 'مرفوضة',
  },

  source: {
    LEGACY: 'من قبل التحوّل إلى SaaS',
    REGISTRATION: 'سجّلت من الموقع',
    PLATFORM: 'أنشأتها فيجانيوم',
  },

  subscriptionStatus: {
    NONE: 'بلا باقة',
    PENDING: 'بانتظار الدفع',
    ACTIVE: 'مدفوع',
    PAST_DUE: 'متأخر',
    CANCELED: 'ملغى',
  },

  invoiceStatus: {
    UNPAID: 'غير مدفوعة',
    PAID: 'مدفوعة',
    VOID: 'ملغاة',
    OVERDUE: 'متأخرة',
  },

  cycles: {
    MONTHLY: 'شهري',
    YEARLY: 'سنوي',
  },

  perCycle: {
    MONTHLY: '{amount} شهرياً',
    YEARLY: '{amount} سنوياً',
  },

  methods: {
    BANK_TRANSFER: 'تحويل بنكي',
    CASH: 'نقداً',
    CHEQUE: 'شيك',
    OTHER: 'أخرى',
  },

  maps: {
    STANDARD: 'الخريطة القياسية',
    GOOGLE: 'خرائط Google',
    noKey: 'لم تُعدّ خرائط Google على الخادم بعد (GOOGLE_MAPS_API_KEY)، لذلك يرى هؤلاء المستخدمون الخريطة القياسية.',
  },

  emailStatus: {
    QUEUED: 'بانتظار الإرسال',
    SENT: 'أُرسلت',
    FAILED: 'تعذّر إرسالها',
    SKIPPED: 'تم تخطيها',
  },

  emailEvents: {
    company: {
      registered: 'استلام طلب التسجيل',
      registeredStaff: 'تسجيل جديد للمراجعة',
      approved: 'الموافقة على الشركة',
      rejected: 'رفض الشركة',
      suspended: 'تعليق الشركة',
      reactivated: 'إعادة تفعيل الشركة',
      trialEnding: 'اقتراب نهاية الفترة التجريبية',
    },
    invoice: {
      issued: 'إصدار فاتورة',
      paid: 'إيصال الدفع',
      dueSoon: 'اقتراب موعد الدفع',
      overdue: 'تأخر الدفع',
    },
    alert: {
      raised: 'تنبيه مركبة',
    },
    email: {
      test: 'رسالة تجريبية',
    },
  },

  dashboard: {
    title: 'نظرة عامة',
    description: 'من ينتظر، ومن يدفع تالياً، وما المستحق.',
    pending: 'بانتظار الموافقة',
    active: 'شركات فعّالة',
    suspended: 'معلّقة',
    mrr: 'الإيراد الشهري المتكرر',
    mrrHint: 'الاشتراكات المدفوعة، والسعر السنوي موزّعاً على اثني عشر شهراً',
    paidThisMonth: 'المستلم هذا الشهر',
    outstanding: 'فواتير غير مدفوعة',
    overdue: 'المتأخر منها: {amount}',
    noMoney: 'لم يُدفع أي اشتراك بعد.',
    waitingTitle: 'بانتظار الموافقة',
    waitingEmpty: 'لا أحد ينتظر. تصل هنا طلبات التسجيل الجديدة من الموقع.',
    renewalsTitle: 'التجديدات خلال 14 يوماً',
    renewalsEmpty: 'لا تنتهي أي فترة مدفوعة خلال الأسبوعين القادمين.',
    trialsTitle: 'الفترات التجريبية المجانية',
    trialsEmpty: 'لا توجد شركة في فترة تجريبية مجانية.',
    trialEnds: 'تنتهي {date}',
    trialEnded: 'آخر يوم {date}',
    overdueTitle: 'فواتير متأخرة',
    overdueEmpty: 'لا شيء متأخر.',
    fleetTitle: 'على VLM اليوم',
    vehicles: 'مركبات فعّالة',
    devices: 'أجهزة تتبع',
    users: 'مستخدمون فعّالون',
    review: 'مراجعة',
    asked: 'طلبت {plan}',
    fleetOf: { zero: 'لا مركبات', one: 'مركبة واحدة', two: 'مركبتان', few: '{count} مركبات', many: '{count} مركبة', other: '{count} مركبة' },
  },

  companies: {
    title: 'الشركات',
    description: 'كل عملاء VLM: وافق على طلبات التسجيل، وخصّص الباقات، وسجّل المدفوعات.',
    add: 'شركة جديدة',
    search: 'ابحث بالاسم أو الرمز أو البريد أو الهاتف',
    empty: 'لا شركات بعد',
    emptyBody: 'تظهر هنا طلبات التسجيل من الموقع، أو أنشئ شركة بنفسك.',
    company: 'الشركة',
    code: 'الرمز',
    contact: 'جهة الاتصال',
    plan: 'الباقة',
    usage: 'المركبات • المستخدمون',
    created: 'أُنشئت',
    tabAll: 'الكل',
    fields: {
      name: 'اسم الشركة',
      contactName: 'الشخص المسؤول',
      email: 'البريد الإلكتروني',
      phone: 'الهاتف',
      city: 'المدينة',
      country: 'الدولة',
      address: 'العنوان',
      taxNumber: 'الرقم الضريبي',
      commercialRegistration: 'السجل التجاري',
      language: 'لغة الفواتير',
      fleetSize: 'حجم الأسطول',
      notes: 'ملاحظات (لفيجانيوم فقط)',
      map: 'الخريطة',
    },
    mapFollowPlan: 'حسب باقتها ({map})',
    mapFollowNoPlan: 'حسب باقتها (الخريطة القياسية إلى أن تُسنَد إليها باقة)',
    mapFollowTrial: 'حسب باقتها (خرائط Google خلال الفترة التجريبية المجانية)',
    mapHint: 'اختر خريطة هنا فقط لتمنح هذه الشركة غير خريطة باقتها.',
    firstAdmin: 'مديرها الأول',
    firstAdminHint: 'يسجّل الدخول إلى لوحة الإدارة بهذا الاسم وكلمة المرور.',
    withAdmin: 'أنشئ مديرها الأول الآن',
    startPlan: 'اشتركها في باقة الآن',
    createdToast: 'أُنشئت {name} • {code}',
    savedToast: 'حُفظت بيانات الشركة',
    editTitle: 'تعديل بيانات الشركة',
  },

  company: {
    overview: 'نظرة عامة',
    billing: 'الباقة والفواتير',
    users: 'المستخدمون',
    approve: 'موافقة',
    reject: 'رفض',
    suspend: 'تعليق',
    reactivate: 'إعادة التفعيل',
    delete: 'حذف الشركة',
    approveTitle: 'الموافقة على {name}',
    approveBody: 'تصبح الشركة فعّالة ويستطيع مستخدموها تسجيل الدخول إلى لوحة الإدارة. ابدأ بها بفترة تجريبية مجانية أو بباقة أو بكليهما — وتُدفع الفترة الأولى من الباقة بتحويل بنكي.',
    approveWithoutPlan: 'موافقة بلا باقة (بلا حدود ولا مستحقات)',
    approveTrialNoPlan: 'بلا باقة الآن — خصّص لها باقة قبل انتهاء الفترة التجريبية',
    approved: 'تمت الموافقة على {name}',
    rejectTitle: 'رفض {name}',
    rejectBody: 'لن يتمكن مستخدموها من تسجيل الدخول. يمكنك الموافقة عليها لاحقاً إن كان ذلك خطأ.',
    rejected: 'رُفضت {name}',
    suspendTitle: 'تعليق {name}',
    suspendBody: 'يُسجَّل خروج مستخدميها فوراً ولا يستطيعون الدخول حتى تعيد تفعيلها. تواصل أجهزتها الإرسال؛ ولا يُحذف شيء.',
    suspended: 'عُلّقت {name}',
    reactivated: 'عادت {name} فعّالة',
    deleteTitle: 'حذف {name}؟',
    deleteBody: 'لا تُحذف إلا شركة بانتظار الموافقة أو مرفوضة، بلا مركبات ولا أجهزة ولا أسيجة ولا فواتير. يُحذف مستخدموها معها. لا يمكن التراجع عن ذلك.',
    deleted: 'حُذفت {name}',
    reason: 'السبب',
    reasonPlaceholder: 'تراه الشركة حين تُبلَّغ بالسبب',
    statusSince: 'السبب: {reason}',
    details: 'البيانات',
    source: 'المصدر',
    registered: 'سجّلت',
    approvedAt: 'تمت الموافقة',
    requested: 'طلبت',
    requestedNone: 'لم تختر باقة',
    usageTitle: 'الاستخدام',
    vehicles: 'مركبات فعّالة',
    devices: 'أجهزة تتبع',
    activeUsers: 'مستخدمون فعّالون',
    ofLimit: '{used} من {limit}',
    notFound: 'هذه الشركة غير موجودة',
    backToList: 'كل الشركات',
    adminLink: 'يسجّل مستخدموها الدخول من لوحة الإدارة',
    emails: 'الرسائل',
    mapFromPlan: '{map} • من باقتها',
    mapSetHere: '{map} • محدَّدة لهذه الشركة',
    mapFromTrial: '{map} • خلال الفترة التجريبية المجانية',
    noLimitTrial: '{used} • بلا حدود خلال الفترة التجريبية',
  },

  subscription: {
    title: 'الباقة',
    none: 'بلا باقة',
    noneBody: 'بلا باقة لا حدود على الشركة ولا مستحقات عليها.',
    noneBodyTrial: 'بعد انتهاء فترتها التجريبية المجانية لا يستطيع مستخدموها تسجيل الدخول إلا بباقة.',
    assign: 'تخصيص باقة',
    change: 'تغيير الباقة',
    cancel: 'إلغاء الاشتراك',
    plan: 'الباقة',
    cycle: 'دورة الفوترة',
    price: 'السعر لكل دورة، قبل الضريبة',
    priceHint: 'اتركه فارغاً لسعر الباقة ({price}).',
    priceHintNone: 'لا سعر لهذه الباقة في هذه الدورة — أدخل سعراً.',
    paidThrough: 'مدفوع حتى',
    notPaidYet: 'لم يُدفع بعد',
    nextPayment: 'الدفعة التالية',
    nextPaymentInvoice: '{number} • تستحق {date}',
    assignedToast: 'حُفظت الباقة',
    changeHint: 'تُطبَّق الباقة الجديدة من الفاتورة التالية، وتسري حدودها فوراً.',
    cancelTitle: 'إلغاء الاشتراك؟',
    cancelBody: 'تحتفظ الشركة بإمكانية الدخول (علّقها لإيقاف ذلك) ولا تصدر فواتير جديدة. تبقى الفواتير غير المدفوعة حتى تلغيها.',
    canceledToast: 'أُلغي الاشتراك',
    started: 'منذ',
    afterTrial: 'تستمر الفترة التجريبية حتى {end}: تسري حدود هذه الباقة بعدها، وتبدأ فترتها الأولى في {start}.',
  },

  trial: {
    title: 'الفترة التجريبية المجانية',
    start: 'ابدأ بفترة تجريبية مجانية',
    give: 'منح فترة تجريبية مجانية',
    giveMore: 'منح أيام إضافية',
    change: 'تعديل الفترة التجريبية',
    end: 'إنهاء الفترة التجريبية',
    days: 'الأيام',
    daysHint: 'من يوم واحد إلى 365 يوماً، واليوم منها.',
    daysFromStart: 'تُحسب من أول يوم فيها، {date}، وهو منها.',
    tooShort: 'لتستمر حتى اليوم يجب ألا تقل أيامها عن {min}.',
    endsOn: 'تنتهي في {date}',
    rules: 'لا حدود لشيء خلال الفترة التجريبية: أي عدد من المركبات والمستخدمين، وخرائط Google ما لم تُحدَّد خريطة هذه الشركة يدوياً. وعند انتهائها تسري باقتها، وإن لم تكن لها باقة فلا يستطيع مستخدموها تسجيل الدخول حتى تخصّص لها باقة أو تمنحها أياماً إضافية.',
    started: 'بدأت',
    lastDay: 'آخر يوم',
    left: 'الأيام المتبقية',
    lengthLabel: 'المدة',
    length: { zero: '{count} يوم', one: 'يوم واحد', two: 'يومان', few: '{count} أيام', many: '{count} يوماً', other: '{count} يوم' },
    daysLeft: {
      zero: 'تجربة مجانية • انتهت',
      one: 'تجربة مجانية • آخر يوم',
      two: 'تجربة مجانية • بقي يومان',
      few: 'تجربة مجانية • بقيت {count} أيام',
      many: 'تجربة مجانية • بقي {count} يوماً',
      other: 'تجربة مجانية • بقي {count} يوم',
    },
    endedNoPlan: 'انتهت التجربة • بلا باقة',
    none: 'لا فترة تجريبية مجانية',
    noneBody: 'امنحها فترة ترفع كل الحدود عدداً من الأيام — مثلاً بينما تجرّب الشركة VLM قبل أن تختار باقة.',
    runningBody: 'كل شيء بلا حدود حتى نهاية يوم {date}، ثم تسري باقتها.',
    runningNoPlan: 'كل شيء بلا حدود حتى نهاية يوم {date}. خصّص لها باقة قبل ذلك، فبعد انتهاء الفترة التجريبية لا يستطيع مستخدموها تسجيل الدخول بلا باقة.',
    endedLocked: 'انتهت الفترة التجريبية — كان آخر يوم فيها {date}. لا يستطيع مستخدموها تسجيل الدخول حتى تخصّص لها باقة أو تمنحها أياماً إضافية.',
    endedWithPlan: 'انتهت الفترة التجريبية — كان آخر يوم فيها {date}، وتسري باقتها.',
    endTitle: 'إنهاء الفترة التجريبية الآن؟',
    endBodyPlan: 'تسري باقتها من الآن: حدودها وخريطتها ودفعتها الأولى.',
    endBodyNoPlan: 'ليست لها باقة، لذا يُسجَّل خروج مستخدميها ولا يستطيعون الدخول حتى تخصّص لها باقة أو تمنحها أياماً إضافية.',
    endedToast: 'انتهت الفترة التجريبية',
    savedToast: 'تستمر الفترة التجريبية حتى {date}',
  },

  invoices: {
    title: 'الفواتير',
    description: 'كل فواتير كل الشركات. يُسجَّل التحويل على فاتورته.',
    search: 'ابحث برقم الفاتورة أو الشركة',
    empty: 'لا فواتير بعد',
    emptyBody: 'أصدرها من تبويب «الباقة والفواتير» لكل شركة.',
    companyEmpty: 'لا فواتير بعد',
    companyEmptyBody: 'أصدر الأولى، أو سجّل تحويلاً وصل بالفعل.',
    number: 'الفاتورة',
    company: 'الشركة',
    period: 'الفترة',
    issueDate: 'أُصدرت',
    dueDate: 'تستحق',
    total: 'الإجمالي',
    paidOn: 'دُفعت في',
    issue: 'إصدار فاتورة',
    recordPayment: 'تسجيل تحويل',
    markPaid: 'تسجيل الدفع',
    void: 'إلغاء',
    downloadEn: 'PDF بالإنجليزية',
    downloadAr: 'PDF بالعربية',
    issueTitle: 'إصدار فاتورة',
    issueBody: 'للفترة التالية من {plan}. تراها الشركة في صفحة الفوترة وتدفعها بتحويل بنكي.',
    payTitle: 'تسجيل التحويل للفاتورة {number}',
    payBody: 'يصبح الاشتراك فعّالاً طوال فترة الفاتورة.',
    recordTitle: 'تسجيل تحويل',
    recordBody: 'تصدر فاتورة الفترة التالية من {plan} وتُسجَّل مدفوعة — فيصبح الاشتراك فعّالاً طوال تلك الفترة.',
    periodStart: 'الفترة من',
    periodEnd: 'الفترة إلى',
    subtotal: 'المبلغ قبل الضريبة',
    subtotalHint: 'اتركه كما هو لسعر الاشتراك.',
    vat: 'ضريبة القيمة المضافة {rate}',
    totalLine: 'الإجمالي مع الضريبة',
    method: 'طريقة الدفع',
    reference: 'مرجع التحويل',
    referenceHint: 'مرجع البنك، ليُعثر على التحويل لاحقاً.',
    note: 'ملاحظة على الدفعة',
    invoiceNote: 'ملاحظة تُطبع على الفاتورة',
    voidTitle: 'إلغاء {number}',
    voidBody: 'تبقى الفاتورة الملغاة محفوظة برقمها، ولا تُدفع أبداً. لا تُلغى فاتورة مدفوعة.',
    voidReason: 'السبب',
    issuedToast: 'أُصدرت {number}',
    paidToast: 'دُفعت {number}',
    voidedToast: 'أُلغيت {number}',
    filterAny: 'أي حالة',
    overdueBadge: 'متأخرة',
    noPlanFirst: 'خصّص باقة قبل الفوترة.',
    notFound: 'هذه الفاتورة غير موجودة',
    invoicePart: 'الفاتورة',
    paymentPart: 'الدفعة',
  },

  plans: {
    title: 'الباقات',
    description: 'ما تشترك فيه الشركات. الباقات العامة المعروضة تظهر على الموقع.',
    add: 'باقة جديدة',
    edit: 'تعديل الباقة',
    empty: 'لا باقات بعد',
    emptyBody: 'أنشئ الباقات التي تختار منها الشركات على الموقع.',
    code: 'الرمز',
    codeHint: 'أحرف إنجليزية صغيرة وأرقام وشرطات — يظهر في روابط الموقع.',
    nameEn: 'الاسم بالإنجليزية',
    nameAr: 'الاسم بالعربية',
    descriptionEn: 'الوصف بالإنجليزية',
    descriptionAr: 'الوصف بالعربية',
    featuresEn: 'المزايا بالإنجليزية',
    featuresAr: 'المزايا بالعربية',
    featuresHint: 'ميزة في كل سطر، كما يعرضها الموقع.',
    currency: 'العملة',
    monthlyPrice: 'السعر الشهري',
    yearlyPrice: 'السعر السنوي',
    priceHint: 'قبل الضريبة. فارغ = لا تُباع بهذه الدورة.',
    maxVehicles: 'المركبات الفعّالة',
    maxUsers: 'المستخدمون الفعّالون',
    limitHint: 'فارغ = بلا حدود.',
    map: 'الخريطة',
    mapHint: 'الخريطة التي يراها مستخدمو الشركات المشتركة فيها. تحاسب Google فيجانيوم على كل خريطة يفتحونها.',
    public: 'على الموقع',
    active: 'معروضة',
    activeHint: 'عند الإيقاف: لا تُخصَّص لشركة جديدة؛ ومن عليها تبقى.',
    featured: 'مميّزة على الموقع',
    sortOrder: 'الترتيب',
    subscribers: { zero: 'لا شركات', one: 'شركة واحدة', two: 'شركتان', few: '{count} شركات', many: '{count} شركة', other: '{count} شركة' },
    prices: 'الأسعار',
    limits: 'الحدود',
    hidden: 'مخفية',
    retired: 'غير معروضة',
    createdToast: 'أُنشئت {name}',
    savedToast: 'حُفظت {name}',
    deleteTitle: 'حذف {name}؟',
    deleteBody: 'لا تُحذف إلا باقة لم تشترك فيها أي شركة. وإلا فأوقف «معروضة».',
    deletedToast: 'حُذفت {name}',
    noPrice: 'لا تُباع',
    notFound: 'هذه الباقة غير موجودة',
  },

  billing: {
    title: 'إعدادات الفوترة',
    description: 'فيجانيوم بصفتها البائع في كل فاتورة، والضريبة، ومهلة الدفع، والحساب الذي تحوّل إليه الشركات.',
    adminOnly: 'لا يغيّرها إلا مدير في المنصّة.',
    seller: 'البائع',
    sellerName: 'اسم البائع',
    sellerAddress: 'عنوان البائع',
    taxNumber: 'الرقم الضريبي',
    commercialRegistration: 'السجل التجاري',
    email: 'بريد الفوترة',
    phone: 'هاتف الفوترة',
    terms: 'الضريبة والشروط',
    vatRate: 'نسبة ضريبة القيمة المضافة (%)',
    currency: 'العملة الافتراضية للباقات الجديدة',
    paymentTermsDays: 'مهلة الدفع بالأيام',
    paymentTermsHint: 'تاريخ الاستحقاق = تاريخ الإصدار + هذه المهلة، ولا يسبق بداية الفترة.',
    bank: 'إلى أين تحوّل الشركات',
    bankName: 'البنك',
    accountName: 'اسم الحساب',
    iban: 'رقم الآيبان',
    swift: 'رمز السويفت',
    invoiceNotes: 'تذييل الفاتورة',
    savedToast: 'حُفظت إعدادات الفوترة',
    snapshotNote: 'تحتفظ الفواتير الصادرة ببيانات البائع وقت إصدارها؛ وتعرض غير المدفوعة الحساب البنكي الحالي دائماً.',
  },

  email: {
    title: 'البريد الإلكتروني',
    description: 'كل رسالة يرسلها VLM إلى الشركات وإلى فيجانيوم عبر ZeptoMail، وما آلت إليه.',
    sending: 'الإرسال',
    sendingHint: 'عبر ZeptoMail',
    notConfigured: 'البريد غير مُعَدّ على الخادم بعد',
    missing: 'الإعدادات الناقصة:',
    notConfiguredBody: 'اضبطها في بيئة الخادم ثم أعد تشغيله. وإلى ذلك الحين لا تخرج أي رسالة من VLM، وتُسجَّل كل رسالة بحالة «تم تخطيها».',
    send: 'إرسال الرسائل',
    onHint: 'مفعّل: تذهب كل رسالة جديدة إلى ZeptoMail، وإن لم يقبلها فوراً أُعيدت محاولة إرسالها.',
    onNotConfigured: 'مفعّل، لكن لا تخرج أي رسالة حتى يُعَدّ البريد على الخادم.',
    offHint: 'موقوف: تُسجَّل الرسائل الجديدة بحالة «تم تخطيها» ولا يُرسَل شيء. ويمكن إعادة إرسال الرسالة المتخطّاة.',
    adminOnly: 'لا يغيّر هذا إلا مدير في المنصّة.',
    offTitle: 'إيقاف إرسال الرسائل؟',
    offBody: 'تُسجَّل الرسائل الجديدة — ومنها الفواتير والتذكيرات — بحالة «تم تخطيها»، ولا تُرسَل ما لم يُعِد أحد إرسالها.',
    turnOff: 'إيقاف الإرسال',
    onToast: 'إرسال الرسائل مفعّل',
    offToast: 'إرسال الرسائل موقوف: تُسجَّل الجديدة بحالة «تم تخطيها»',
    from: 'المرسِل',
    replyTo: 'تذهب الردود إلى',
    replyToNone: 'حدّد بريداً للفوترة',
    endpoint: 'يُرسَل عبر',
    notSet: 'غير محدّد',
    lastSevenDays: 'آخر 7 أيام',
    recipients: 'من يستلم ماذا',
    recipientsBilling: 'الفواتير والإيصالات وتذكيرات الدفع: بريد الشركة (بريد الفوترة)، أو مديروها الفعّالون ما دام لا بريد لها.',
    recipientsAccount: 'الموافقة والرفض والتعليق وإعادة التفعيل: مديرو الشركة الفعّالون.',
    recipientsRegistration: 'طلب التسجيل: من سجّل، وكل مستخدمي المنصّة الفعّالين.',
    recipientsAlerts: 'تنبيهات المركبات: العناوين التي يحددها مديرو كل شركة لكل نوع من التنبيهات في إدارة VLM › إعدادات التنبيهات.',
    test: 'أرسل لي رسالة تجريبية',
    testSent: 'أُرسلت الرسالة التجريبية إلى {address}',
    testQueued: 'الرسالة التجريبية إلى {address} بانتظار الإرسال؛ يعيد VLM المحاولة بعد قليل.',
    messages: 'الرسائل',
    search: 'ابحث بالعنوان أو الاسم أو الموضوع أو الشركة',
    when: 'الوقت',
    to: 'إلى',
    subject: 'الموضوع',
    company: 'الشركة',
    resend: 'إعادة الإرسال',
    queuedFor: 'الرسالة إلى {address} بانتظار الإرسال مجدداً',
    sentTo: 'أُرسلت إلى {address}',
    notSentTo: 'لم تُرسَل الرسالة إلى {address}.',
    attemptCount: { zero: 'لا محاولات', one: 'محاولة واحدة', two: 'محاولتان', few: '{count} محاولات', many: '{count} محاولة', other: '{count} محاولة' },
    empty: 'لا رسائل بعد',
    emptyBody: 'تظهر هنا رسائل التسجيل والموافقة والفواتير والتذكيرات حين يرسلها VLM.',
    companyEmpty: 'لا رسائل إلى {name} بعد',
    companyEmptyBody: 'تظهر هنا رسائل حسابها وفوترتها.',
    thisCompany: 'هذه الشركة',
    removeCompany: 'عرض رسائل كل الشركات',
    event: 'الحدث',
    attempts: 'المحاولات',
    created: 'أُنشئت',
    sent: 'أُرسلت',
    nextAttempt: 'المحاولة التالية',
    attachments: 'المرفقات',
    preview: 'المعاينة',
    previewAs: 'اعرض الرسالة',
    html: 'منسّقة',
    text: 'نص عادي',
    previewTitle: 'الرسالة «{subject}»',
    noHtml: 'أُرسلت هذه الرسالة نصاً عادياً فقط.',
    noText: 'لا نسخة نصية عادية لهذه الرسالة.',
    lastError: {
      QUEUED: 'سبب فشل المحاولة الأخيرة',
      SENT: 'ردّ ZeptoMail',
      FAILED: 'سبب الفشل',
      SKIPPED: 'سبب التخطي',
    },
  },

  users: {
    title: 'مستخدمو المنصّة',
    description: 'موظفو فيجانيوم الذين يسجّلون الدخول هنا. يُدار مستخدمو الشركات من صفحة كل شركة.',
    add: 'إضافة مستخدم للمنصّة',
    edit: 'تعديل مستخدم المنصّة',
    username: 'اسم المستخدم',
    usernameHint: 'من 3 إلى 50 حرفاً إنجليزياً أو رقماً أو نقطة أو شرطة.',
    usernameLocked: 'لا يمكن تغيير اسم المستخدم.',
    fullName: 'الاسم الكامل',
    email: 'البريد الإلكتروني',
    role: 'الدور',
    password: 'كلمة المرور',
    passwordHint: '8 أحرف على الأقل.',
    lastLogin: 'آخر تسجيل دخول',
    you: 'أنت',
    createdToast: 'أُضيف {name}',
    savedToast: 'حُفظ {name}',
    activated: 'يستطيع {name} تسجيل الدخول مجدداً',
    deactivated: 'لم يعد {name} يستطيع تسجيل الدخول',
    activateTitle: 'السماح لـ {name} بتسجيل الدخول مجدداً؟',
    activateBody: 'يسجّل الدخول بكلمة مروره الحالية.',
    deactivateTitle: 'منع {name} من تسجيل الدخول؟',
    deactivateBody: 'يُسجَّل خروجه من كل مكان فوراً.',
    setPassword: 'تعيين كلمة مرور جديدة',
    setPasswordTitle: 'كلمة مرور جديدة لـ {name}',
    setPasswordBody: 'يُسجَّل خروجه من كل مكان، ويدخل بهذه الكلمة.',
    passwordSet: 'عُيّنت كلمة المرور الجديدة',
    notFound: 'هذا المستخدم غير موجود',
  },

  companyUsers: {
    title: 'مستخدمو {name}',
    description: 'الأدوار نفسها كما في لوحة الإدارة. لا يُلزم حدّ المستخدمين في الباقة فيجانيوم.',
    add: 'إضافة مستخدم',
    edit: 'تعديل المستخدم',
    empty: 'لا مستخدمين',
    emptyBody: 'أضف المدير الأول للشركة ليتمكن من تسجيل الدخول.',
    passwordSetBody: 'تستمر جلساته المفتوحة حتى تنتهي؛ ويدخل بهذه الكلمة من الآن.',
  },

  profile: {
    title: 'ملفك الشخصي',
    description: 'بياناتك وكلمة مرورك.',
    details: 'البيانات',
    password: 'كلمة المرور',
    currentPassword: 'كلمة المرور الحالية',
    newPassword: 'كلمة المرور الجديدة',
    confirmPassword: 'كلمة المرور الجديدة مرة أخرى',
    mismatch: 'كلمتا المرور مختلفتان',
    changePassword: 'تغيير كلمة المرور',
    changed: 'تغيّرت كلمة المرور — سُجّل الخروج من كل الجلسات الأخرى',
    saved: 'حُفظت بياناتك',
  },

  errors: {
    invalidCredentials: 'اسم المستخدم وكلمة المرور غير متطابقين.',
    accountDisabled: 'هذا الحساب موقوف.',
    rateLimited: 'محاولات كثيرة. انتظر بضع دقائق وحاول مجدداً.',
    usernameTaken: 'اسم المستخدم هذا مستخدم.',
    planCodeTaken: 'لباقة أخرى هذا الرمز.',
    planInUse: 'اشتركت شركات في هذه الباقة، فلا يمكن حذفها. أوقف «معروضة» بدلاً من ذلك.',
    lastAdmin: 'يجب أن يبقى مدير فعّال واحد على الأقل.',
    periodInvoiced: 'هناك فاتورة تغطي جزءاً من هذه الفترة بالفعل.',
    priceRequired: 'لا سعر لهذه الباقة في هذه الدورة — أدخل سعراً.',
    notFound: 'لم يعد هذا موجوداً. حدّث الصفحة.',
    accessDenied: 'دورك لا يسمح بذلك.',
    network: 'تعذّر الوصول إلى VLM. تحقق من اتصالك.',
    server: 'واجه VLM مشكلة ({status}). حاول مجدداً.',
    unknown: 'حدث خطأ ما. حاول مجدداً.',
    validation: 'راجع الحقول المحدّدة.',
  },

  pages: {
    forbiddenTitle: 'ليست لدورك',
    forbiddenBody: 'يفتح هذه الصفحة مدير في المنصّة.',
    notFoundTitle: 'لا شيء هنا',
    notFoundBody: 'الصفحة التي طلبتها غير موجودة.',
    goHome: 'الذهاب إلى النظرة العامة',
    staleTitle: 'لا شيء لتفعله هنا الآن',
    staleBody: '{name} الآن: {status}.',
  },
};
